import { ProjectContext } from "context/ProjectProvider";
import { useCIQQuery } from "hooks/ciq-gql-hooks";
import {
  forwardRef,
  useContext,
  useEffect,
  useImperativeHandle,
  useState
} from "react";
import { QUERY_GET_PROJECT_SPECS } from "services/graphQL/queries";
import { Button, Divider, Select } from "antd";

export const featureSpecSectionDropdownRenderer = (
  menu: any,
  onCreateSpecSectionClick: () => void
) => (
  <>
    {menu}
    <Divider className="my-1" style={{ margin: "8px 0" }} />
    <div className="w-full px-1">
      <Button
        className="rounded-none  bg-ciq-gray-800 bg-opacity-25 hover:bg-opacity-40 w-full"
        onClick={() => {
          if (onCreateSpecSectionClick) {
            onCreateSpecSectionClick();
          }
        }}
        data-testid="index-button-add-new-spec-section"
      >
        Add New Spec Section
      </Button>
    </div>
  </>
);

const FeatureSpecSectionDropdown = forwardRef(
  function FeatureSpecSectionDropdown(
    props: {
      disabled?: boolean;
      selectedIds?: any;
      onBlur: (ids: string[], option: any) => void;
      dropdownRender?: (menu: any) => React.ReactElement;
      mode?: "multiple" | "single";
      className?: string;
      open?: boolean;
      setSpecSections?: React.Dispatch<React.SetStateAction<any[]>>;
    },
    ref: any
  ) {
    const {
      onBlur,
      disabled,
      selectedIds,
      dropdownRender,
      mode = "multiple",
      className,
      open,
      setSpecSections
    } = props;

    const { gqlClientForProject, eventLogs } = useContext(ProjectContext);

    const [localSelectedIds, setLocalSelectedIds] = useState<string[]>([]);
    const [localOptions, setLocalOptions] = useState<any>();

    const {
      data: specSections,
      refetch: refetchSpecs,
      loading
    } = useCIQQuery<{
      spec_sections: Array<{
        title: string;
        id: string;
        section_number: string;
      }>;
    }>(QUERY_GET_PROJECT_SPECS, {
      client: gqlClientForProject,
      skip: !gqlClientForProject
    });

    useImperativeHandle(ref, () => ({
      refetchSpecs
    }));

    useEffect(() => {
      if (!selectedIds || !specSections) return;
      if (setSpecSections) {
        setSpecSections(specSections?.spec_sections);
      }
      setLocalSelectedIds(structuredClone(selectedIds));
    }, [selectedIds, setSpecSections, specSections]);

    useEffect(() => {
      const hasInsertEvent = eventLogs.some(
        (log) =>
          log.data_source === "spec_sections" && log.change_type === "insert"
      );
      if (hasInsertEvent) {
        refetchSpecs();
      }
    }, [eventLogs, refetchSpecs]);

    return (
      <Select
        showSearch
        mode={mode === "single" ? undefined : "multiple"}
        disabled={disabled}
        value={localSelectedIds}
        filterOption={(inputValue, option: any) =>
          option.label.toLowerCase().includes(inputValue.toLowerCase())
        }
        options={specSections?.spec_sections.map((spec) => ({
          value: spec.id,
          label: spec.title
            ? `${spec.section_number} - ${spec.title}`
            : spec.section_number
        }))}
        onChange={(ids: any, op: any) => {
          if (mode === "multiple") {
            setLocalSelectedIds(ids);
            setLocalOptions(op);
          } else {
            setLocalSelectedIds(ids ? [ids] : []);
            setLocalOptions(op ? [op] : []);
            onBlur(ids ? [ids] : [], op ? [op] : []);
          }
        }}
        onBlur={() => {
          if (mode === "multiple") {
            onBlur(localSelectedIds, localOptions);
          }
        }}
        removeIcon={null}
        showArrow
        dropdownRender={dropdownRender}
        className={className}
        open={open}
        loading={loading}
        notFoundContent={loading ? null : "No spec sections found"}
        data-testid="select-spec-section"
        allowClear={mode === "single"}
      />
    );
  }
);

export default FeatureSpecSectionDropdown;
